import { useRef, useState, useCallback, useMemo } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faGear } from '@fortawesome/pro-solid-svg-icons/faGear';
import { FormattedMessage, defineMessages, useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import { BannerStatus } from 'ui-components/components/banner-message/banner-enums';
import { BannerMessageV2 } from 'ui-components/components/banner-message/banner-message-v2';
import { Button } from 'ui-components/components/button';
import { FloatInput } from 'ui-components/components/float-input';
import { LoadingIndicator } from 'ui-components/components/loading-indicator';
import { Modal } from 'ui-components/components/modal';
import { useModalContext } from 'ui-components/components/modal/context';
import { ModalBody } from 'ui-components/components/modal/modal-body';
import { ModalFooter } from 'ui-components/components/modal/modal-footer';
import { ModalHeader } from 'ui-components/components/modal/modal-header';

import style from './map-order-import.module.less';
import { useOnComponentMount } from '../../../hooks/use-on-component-mount';
import Global from '../../../store/global-selectors';
import * as Actions from '../../../store/orders/import/orders.import.actions';
import { Workflows } from '../../../tracking/tracking-constants';
import { useTrackWorkflow, WorkflowSteps } from '../../../tracking/use-workflow-tracking';
import { useOrderCSVImportContext } from '../../order-import/order-import-context';
import OrderImportColumnMapper from '../../order-import-column-mapper';

const messages = defineMessages({
  mappingName: {
    description: 'mapOrderImportModal.fieldMappingName',
    defaultMessage: 'Field mapping name',
    id: 'kxNJ0r',
  },
  mapField: {
    description: 'mapOrderImportModal.mapField',
    defaultMessage: 'Required: Please map { name }',
    id: 'IcB0fI',
  },
  fieldDuplicate: {
    description: 'mapOrderImportModal.fieldDuplicate',
    defaultMessage: 'Duplicate: Please map { duplicate } to only one field',
    id: 'rnAEGE',
  },
  mappedHeadingOrderInfo: {
    description: 'mapOrderImportModal.mappedHeadingOrderInfo',
    defaultMessage: 'Order Information',
    id: 'Fi+61p',
  },
  mappedHeadingBuyerReceipientInfo: {
    description: 'mapOrderImportModal.mappedHeadingBuyerReceipientInfo',
    defaultMessage: 'Buyer & Recipient Information',
    id: '9JP9sE',
  },
  mappedHeadingiteminfo: {
    description: 'mapOrderImportModal.mappedHeadingiteminfo',
    defaultMessage: 'Item Information',
    id: 'm8y6B/',
  },
  mappedheadingNotes: {
    description: 'mapOrderImportModal.mappedheadingNotes',
    defaultMessage: 'Notes',
    id: 'L64diN',
  },
});

export const MapOrderImportModal = () => {
  const dispatch = useDispatch();
  const isLoading = useSelector(Global.selectOrdersImportIsLoading);
  const importedCols = useSelector(Global.selectOrdersImportImportedCols);
  const shipStationCols = useSelector(Global.selectOrdersImportShipStationCols);
  const importGuid = useSelector(Global.selectOrdersImportBatchNumber);
  const globalMappingName = useSelector(Global.selectOrdersImportMappingName);
  const activeStores = useSelector(Global.selectSellerActiveManualStores);
  const selectedStore = useSelector((state) => Global.selectOrdersImportSelectedStore(state, activeStores));
  const { closeCSVImport } = useOrderCSVImportContext();
  const { onClose } = useModalContext();

  const intl = useIntl();
  const scrollErrorIntoView = useRef();
  const [error, setError] = useState([]);
  const [mappingName, setMappingName] = useState(globalMappingName);
  const [mappings, setMappings] = useState({});
  const [expandKey, setExpandKey] = useState('Order');
  const trackOrderImport = useTrackWorkflow(Workflows.ORDER_IMPORT_FLOW);

  useOnComponentMount(() => {
    const mappedFields = importedCols.filter((col) => col.mappedToField !== null && col.mappedToField !== undefined);

    const newMappings = { ...mappings };

    mappedFields.forEach((mappedField) => {
      mappedField.mappedToField.split('|').forEach((mappedToField) => {
        const ssField = shipStationCols.find(({ field }) => field === mappedToField);
        if (ssField) {
          newMappings[ssField.name] = mappedField.name;
        }
      });
    });

    // If the user didn't select a field mapping, use a default mapping that maps the exact CSV names to the display names
    // Checking mappingName for an empty string is consistent with the backend:
    // https://github.com/shipstation/shipstation/blob/1084119077c87282170b23a095095f0aaf0e87a9/SS.Core/Objects/Orders.cs#L1724
    if (mappingName === '' && Object.keys(newMappings).length === 0) {
      const importedColsNames = importedCols.map((x) => x.name);
      const defaultMappings = shipStationCols.reduce((allMappings, c) => {
        const modifiedMappings = { ...allMappings };
        if (importedColsNames.includes(c.name)) {
          modifiedMappings[c.name] = c.name;
        } else if (importedColsNames.includes(c.field)) {
          modifiedMappings[c.name] = c.field;
        }
        return modifiedMappings;
      }, {});
      setMappings(defaultMappings);
    } else {
      setMappings(newMappings);
    }
  });

  const setMatchingCSVField = useCallback(
    (ssField, csvField) => {
      trackOrderImport(WorkflowSteps.ORDER_IMPORT_MAPPING_SELECTION, {
        ssField,
        csvField,
      });
      setMappings((m) => {
        const modifiedMapping = { ...m };
        modifiedMapping[ssField] = csvField;
        if (!csvField) {
          delete modifiedMapping[ssField];
        }
        return modifiedMapping;
      });
    },
    [trackOrderImport],
  );

  const getMatchingCSVField = useCallback(
    (ssField) => {
      const csvField = mappings[ssField];

      if (!csvField) return { name: '', mappedToField: null };

      return importedCols.find((col) => col.name === csvField);
    },
    [importedCols, mappings],
  );

  const setExpander = useCallback((newKey) => {
    setExpandKey((key) => {
      if (key === newKey) {
        return '';
      }
      return newKey;
    });
  }, []);

  const generateMappingOutput = useCallback(() => {
    return shipStationCols.reduce((acc, currentItem) => {
      const csvField = mappings[currentItem.name] ?? mappings[currentItem.field];

      return `${acc}{${currentItem.field},${csvField || ''}}|`;
    }, '');
  }, [mappings, shipStationCols]);

  const errorChecker = useCallback(
    (ssColumns) => {
      return Object.keys(ssColumns)
        .map((key) => ssColumns[key])
        .reduce((acc, { isRequired, name }) => {
          if (isRequired && !mappings[name]) {
            acc.push(intl.formatMessage(messages.mapField, { name }));
          }
          return acc;
        }, []);
    },
    [intl, mappings],
  );

  const handleFileImport = useCallback(() => {
    const errors = errorChecker(shipStationCols, intl);
    if (errors.length) {
      setError(errors);
      const node = scrollErrorIntoView.current;
      node.scrollIntoView();
      return;
    }
    const newMappings = generateMappingOutput();
    trackOrderImport(WorkflowSteps.ORDER_IMPORT_START_IMPORT, {
      mappings: newMappings,
      mappingName,
    });
    dispatch(
      Actions.startFileImport({
        storeGuid: selectedStore.storeGuid,
        importGuid,
        mappings: newMappings,
        importMappingName: mappingName,
      }),
    );
    closeCSVImport();
  }, [
    dispatch,
    errorChecker,
    generateMappingOutput,
    importGuid,
    intl,
    mappingName,
    closeCSVImport,
    selectedStore.storeGuid,
    shipStationCols,
    trackOrderImport,
  ]);

  const onClickBack = useCallback(() => {
    trackOrderImport(WorkflowSteps.ORDER_IMPORT_BACK_CLICK);
    onClose();
  }, [onClose, trackOrderImport]);

  const shipStationColsGroupings = useMemo(
    () => [
      {
        fieldTitle: intl.formatMessage(messages.mappedHeadingOrderInfo),
        cols: shipStationCols.filter((col) => col.columnType === 'Order'),
        key: 'Order',
      },
      {
        fieldTitle: intl.formatMessage(messages.mappedHeadingBuyerReceipientInfo),
        cols: shipStationCols.filter((col) => col.columnType === 'Receipt'),
        key: 'Receipt',
      },
      {
        fieldTitle: intl.formatMessage(messages.mappedHeadingiteminfo),
        cols: shipStationCols.filter((col) => col.columnType === 'Item'),
        key: 'Item',
      },
      {
        fieldTitle: intl.formatMessage(messages.mappedheadingNotes),
        cols: shipStationCols.filter((col) => col.columnType === 'Note'),
        key: 'Note',
      },
    ],
    [intl, shipStationCols],
  );

  return (
    <Modal className={style['map-order-import-modal']} size='medium'>
      <ModalHeader>
        <FormattedMessage defaultMessage='Import Orders' description='mapOrderImportModal.title' id='TKrBJD' />
      </ModalHeader>
      <ModalBody className={style['map-order-import-modal-body']}>
        {isLoading ? <LoadingIndicator className={style['loading-indicator']} /> : null}
        {!isLoading && (
          <div>
            <div className={style['map-order-import-body-content']}>
              <div ref={scrollErrorIntoView} />
              <div className={style['map-order-import-heading-instruction']}>
                <FormattedMessage
                  defaultMessage='Map your column headers to the appropriate ShipStation fields'
                  description='mapOrderImportModal.instruction'
                  id='vLXWEF'
                />
              </div>
              <p className={style['map-order-import-sub-heading']}>
                <span className={style['map-order-item-icon']}>
                  <FontAwesomeIcon className={style.icon} icon={faGear} />
                </span>
                <span>{selectedStore ? selectedStore.name : null}</span>
              </p>
              {error.length > 0 && (
                <BannerMessageV2
                  className={style['map-order-import-error']}
                  content={
                    <>
                      {error.map((err) => (
                        <p key={err} className={style['map-order-import-error-item']}>
                          {err}
                        </p>
                      ))}
                    </>
                  }
                  status={BannerStatus.Error}
                />
              )}

              <OrderImportColumnMapper
                expandKey={expandKey}
                getMatchingCSVField={getMatchingCSVField}
                importedCols={importedCols}
                setExpandKey={setExpander}
                setMatchingCSVField={setMatchingCSVField}
                shipStationColsGroupings={shipStationColsGroupings}
              />
            </div>
            {!isLoading && (
              <div className={style['fixed-modal-body-bottom']}>
                <div className={style['modal-body-bottom-first-column']}>
                  <FloatInput
                    label={intl.formatMessage(messages.mappingName)}
                    onChange={(value) => setMappingName(value)}
                    value={mappingName}
                  />
                </div>
                <p className={style['modal-body-bottom-second-column']}>
                  <FormattedMessage
                    defaultMessage="Save import mapping as (leave this blank if you don't want to save it)"
                    description='mapOrderImportModal.fieldMappingBlurp'
                    id='k8FLRl'
                  />
                </p>
              </div>
            )}
          </div>
        )}
      </ModalBody>
      {!isLoading && (
        <ModalFooter className={style['map-order-import-modal-footer']}>
          <Button onClick={onClickBack} theme='tertiary'>
            <FormattedMessage defaultMessage='Back' description='mapOrderImportModal.back' id='TIKt/p' />
          </Button>
          <div className={style['start-import-button-text']}>
            <Button onClick={handleFileImport} theme='primary'>
              <FormattedMessage defaultMessage='Start Import' description='mapOrderImportModal.startImport' id='RTEqBo' />
            </Button>
          </div>
        </ModalFooter>
      )}
    </Modal>
  );
};
